<script>
import { h, Fragment } from 'vue'
import { useComponentPresetProp } from '../../composables/useComponentPreset.ts'

const getPropsData = (slot) => slot?.props

const getIsActive = (slot) => !!slot?.props?.active || false

/**
* Slot can be Vue Fragment generated by v-for.
* If slot `type` property is Vue Fragment Symbol return true
*/
const isVueFragment = (slot) => slot.type === Fragment

const isEmptyArray = (arr) => arr && arr.length === 0

/* Returns correct slots without v-for vue fragment */
function getSlots (slots) {
  const defaultSlots = slots.default?.()

  if (!defaultSlots || isEmptyArray(defaultSlots)) { return [] }
  if (isVueFragment(defaultSlots[0])) { return defaultSlots[0].children }

  return defaultSlots
}

const processSlots = (context) => {
  const slots = getSlots(context.slots)

  slots.forEach((slot, index) => {
    if (!getPropsData(slot)) {
      slot.props = {}
    }

    const propsData = getPropsData(slot)

    // Pass down vertical prop.
    propsData.vertical = context.props.vertical

    if (context.props.centered) {
      // Every second slot will be inverted
      propsData.inverted = !!(index % 2)
    }

    if (index === 0) {
      propsData.isFirst = true
    }
    if (index === slots.length - 1) {
      propsData.isLast = true
    }

    const currentSlotActive = propsData.active

    // For inactive slot props are default
    if (!currentSlotActive) {
      return
    }

    if (index === 0) {
      propsData.activePrevious = currentSlotActive
    }

    if (index === slots.length - 1) {
      propsData.activeNext = currentSlotActive
    }

    const previousSlotActive = getIsActive(slots[index - 1])
    if (previousSlotActive) {
      propsData.activePrevious = true
    }

    const nextSlotActive = getIsActive(slots[index + 1])
    if (nextSlotActive) {
      propsData.activeNext = true
    }
  })

  return slots
}

const COMPONENT_NAME = 'va-timeline'

export default {
  name: COMPONENT_NAME,
  props: {
    ...useComponentPresetProp,
    vertical: { type: Boolean },
    centered: { type: Boolean },
    alignTop: { type: Boolean },
  },
  setup (props, { slots }) {
    return () => h(
      'div',
      {
        class: {
          [COMPONENT_NAME]: true,
          [`${COMPONENT_NAME}--vertical`]: props.vertical,
          [`${COMPONENT_NAME}--align-top`]: props.alignTop,
        },
      },
      processSlots({ props, slots }),
    )
  },
}
</script>

<style lang="scss">
@import 'variables';

.va-timeline {
  display: var(--va-timeline-display);
  flex-wrap: var(--va-timeline-flex-wrap);
  font-family: var(--va-font-family);

  &--vertical {
    flex-direction: var(--va-timeline-vertical-flex-direction);
    padding-left: var(--va-timeline-vertical-padding-left);
    padding-right: var(--va-timeline-vertical-padding-right);
  }

  &--align-top {
    .va-timeline-item__before,
    .va-timeline-item__after {
      flex: 0;
    }
  }

  .va-timeline-item {
    flex: 1;
  }

  .va-timeline-item--vertical {
    .va-timeline-separator--vertical .va-timeline-separator__line {
      &:first-child {
        flex: 0 0 1rem;
      }
    }

    &.va-timeline-item--is-first {
      .va-timeline-separator--vertical .va-timeline-separator__line {
        &:first-child {
          flex-basis: 2rem;
        }
      }

      .va-timeline-item__after,
      .va-timeline-item__before {
        padding-top: 2rem;
      }
    }

    &.va-timeline-item--vertical.va-timeline-item--is-last {
      .va-timeline-item__after,
      .va-timeline-item__before {
        padding-bottom: 2rem;
      }
    }
  }
}
</style>
